import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Siren, MapPin, Phone, CheckCircle2, Loader2, AlertOctagon, Heart, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import PageHero from "@/components/PageHero";

const CATEGORIES = [
  { value: "medical", label: "Medical" },
  { value: "fire", label: "Fire" },
  { value: "accident", label: "Accident" },
  { value: "harassment", label: "Harassment / Safety" },
  { value: "general", label: "General emergency" },
];

export default function SOS() {
  const [coords, setCoords] = useState(null);
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [holding, setHolding] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [story, setStory] = useState({ quote: "", city: "", stars: 5 });
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("g360_story_dismissed") || "[]")); } catch { return new Set(); }
  });
  const holdTimer = useRef(null);

  const load = async () => {
    const { data } = await api.get("/sos/mine");
    setAlerts(data);
  };

  useEffect(() => {
    load();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setCoords({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
        () => setCoords({ latitude: 12.9716, longitude: 77.5946 }),
        { enableHighAccuracy: true }
      );
    } else {
      setCoords({ latitude: 12.9716, longitude: 77.5946 });
    }
  }, []);

  const trigger = async () => {
    if (!coords) {
      toast.error("Location not available yet");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sos", { ...coords, category, message: message || undefined });
      toast.success("SOS broadcasted. Help is on the way.");
      setMessage("");
      load();
    } catch (e) {
      toast.error("Failed to send SOS");
    } finally {
      setBusy(false);
    }
  };

  const startHold = () => {
    setHolding(true);
    holdTimer.current = setTimeout(() => {
      setHolding(false);
      trigger();
    }, 1500);
  };
  const cancelHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    setHolding(false);
  };

  const resolve = async (id) => {
    await api.post(`/sos/${id}/resolve`);
    toast.success("Marked as resolved");
    load();
    // Prompt for a testimonial at the moment of highest gratitude — but only once per alert.
    if (!dismissedIds.has(id)) {
      const next = new Set(dismissedIds);
      next.add(id);
      setDismissedIds(next);
      localStorage.setItem("g360_story_dismissed", JSON.stringify([...next]));
      setStory({ quote: "", city: "", stars: 5 });
      setStoryOpen(true);
    }
  };

  const submitStory = async () => {
    if (story.quote.trim().length < 20) return toast.error("Please write at least 20 characters");
    try {
      await api.post("/testimonials", story);
      toast.success("Thank you for sharing your story. Our team will review it shortly.");
      setStoryOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <div className="max-w-6xl">
      <PageHero
        kicker="Emergency SOS"
        title="Hold to send SOS"
        subtitle="Your live location + selected category will be shared instantly with volunteers and family."
        image="https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
        accent="destructive"
        testId="sos-hero"
      />
      <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--destructive))] mb-2 font-semibold">Emergency SOS</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-2">Hold to send SOS</h1>
        <p className="text-muted-foreground mb-6">Your live location + selected category will be shared instantly with volunteers and family.</p>

        <div className="rounded-3xl bg-card border border-border/70 p-8 card-shadow relative overflow-hidden">
          <div className="grid gap-4 mb-6">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="sos-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Message (optional)</label>
              <Textarea data-testid="sos-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the situation briefly..." />
            </div>
          </div>

          <div className="flex flex-col items-center py-4">
            <motion.button
              data-testid="sos-trigger-button"
              onMouseDown={startHold} onMouseUp={cancelHold} onMouseLeave={cancelHold}
              onTouchStart={startHold} onTouchEnd={cancelHold}
              disabled={busy}
              whileTap={{ scale: 0.96 }}
              className={`sos-pulse relative w-48 h-48 rounded-full bg-[hsl(var(--destructive))] text-white font-display font-black text-3xl tracking-tighter grid place-items-center select-none disabled:opacity-60 ${holding ? "scale-105" : ""}`}
              style={{ transition: "transform 1.4s ease" }}
            >
              {busy ? <Loader2 className="w-10 h-10 animate-spin" /> : holding ? "…" : (<div className="flex flex-col items-center"><Siren className="w-10 h-10 mb-1" />SOS</div>)}
            </motion.button>
            <div className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {coords ? `${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}` : "Locating..."}
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <a href="tel:911" className="flex-1"><Button variant="outline" className="w-full"><Phone className="w-4 h-4 mr-2" /> Call 911</Button></a>
          <a href="tel:112" className="flex-1"><Button variant="outline" className="w-full"><Phone className="w-4 h-4 mr-2" /> Call 112</Button></a>
        </div>
      </div>

      <div>
        <div className="mb-4">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold">Live map</div>
          <div className="rounded-2xl overflow-hidden border border-border/70" style={{ height: 260 }}>
            {coords ? (
              <LiveMap points={[{ lat: coords.latitude, lng: coords.longitude, label: "You are here", color: "primary" }]} center={[coords.latitude, coords.longitude]} zoom={14} />
            ) : (
              <div className="w-full h-full grid place-items-center text-muted-foreground text-sm bg-muted">Locating...</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border/70 bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-lg">Your alerts</div>
            <Badge variant="outline">{alerts.length}</Badge>
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">No SOS alerts yet.</div>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {alerts.map(a => (
                <div key={a.id} data-testid={`sos-item-${a.id}`} className="p-3 rounded-xl bg-muted/60 flex items-start gap-3">
                  <AlertOctagon className={`w-4 h-4 mt-0.5 ${a.status === "active" ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--secondary))]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium capitalize">{a.category}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.message || "No message"}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                  {a.status === "active" && (
                    <Button size="sm" variant="ghost" onClick={() => resolve(a.id)} data-testid={`resolve-sos-${a.id}`}>
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Post-resolve testimonial prompt */}
      <Dialog open={storyOpen} onOpenChange={setStoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[hsl(var(--destructive))]" />
              Glad you're safe. Share what happened?
            </DialogTitle>
            <DialogDescription>
              A short story helps other families feel confident about Guardian360. If approved by our team, it may appear on the homepage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Your story (min. 20 chars)</Label>
              <Textarea data-testid="sos-story-quote" rows={5} value={story.quote} onChange={(e) => setStory({ ...story, quote: e.target.value })} placeholder="Tonight, Guardian360…" />
            </div>
            <div>
              <Label>City (optional)</Label>
              <Input data-testid="sos-story-city" value={story.city} onChange={(e) => setStory({ ...story, city: e.target.value })} placeholder="Mumbai" />
            </div>
            <div>
              <Label>How was your experience?</Label>
              <div className="flex gap-1 mt-1" data-testid="sos-story-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setStory({ ...story, stars: n })}
                    className={`text-2xl ${n <= story.stars ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>★</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid="sos-story-skip" variant="outline" className="flex-1" onClick={() => setStoryOpen(false)}>
                Not now
              </Button>
              <Button data-testid="sos-story-submit" onClick={submitStory} className="flex-1 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
                <Quote className="w-4 h-4 mr-2" /> Share my story
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
